import {Session} from "@m2c2kit/session";
import {SymbolSearch} from "./index";

const activity = new SymbolSearch();

// Set game parameters from URL parameters
const urlParams = new URLSearchParams(window.location.search);
const number_of_trials_count = 11; // 시도 횟수
const gameParameters: { [key: string]: string } = {
    "number_of_trials": number_of_trials_count.toString()
};
urlParams.forEach((value, key) => {
    gameParameters[key] = value;
});
activity.setParameters(gameParameters);

const session = new Session({
    activities: [activity],
});

type CardSymbol = {
    top: number;
    bottom: number;
};

type CardConfiguration = {
    top_cards_symbols: CardSymbol[];
    bottom_cards_symbols: CardSymbol[];
};

type DeviceMetadata = {
    userAgent: string;
    devicePixelRatio: number;
    screen: {
        availHeight: number;
        availWidth: number;
        colorDepth: number;
        height: number;
        orientation: {
            type: string;
            angle: number;
        };
        pixelDepth: number;
        width: number;
    };
    webGlRenderer: string;
};

type Trial = {
    document_uuid: string;
    study_id: string | null;
    study_uuid: string | null;
    session_uuid: string;
    activity_uuid: string;
    activity_id: string;
    activity_publish_uuid: string;
    activity_version: string;
    device_timezone: string;
    device_timezone_offset_minutes: number;
    locale: string;
    activity_begin_iso8601_timestamp: string;
    trial_begin_iso8601_timestamp: string;
    trial_end_iso8601_timestamp: string;
    trial_index: number;
    trial_type: string;
    card_configuration: CardConfiguration;
    response_time_duration_ms: number;
    user_response_index: number;
    correct_response_index: number;
    quit_button_pressed: boolean;
    device_metadata: DeviceMetadata;
    ActivityKeyValueData: any;
};

type InputData = {
    trials: Trial[];
};

interface FlutterChannel {
    postMessage(message: string): void; // Flutter로 메시지를 전달하는 메서드
}

// 글로벌 window 객체 확장
declare global {
    interface Window {
        FlutterChannel?: FlutterChannel; // Flutter WebView와의 통신 채널
    }
}

function sendDataToFlutter(data: any) {
    console.log("sendDataToFlutter= " + JSON.stringify(data));

    if (window.FlutterChannel && typeof window.FlutterChannel.postMessage === "function") {
        console.log("FlutterChannel is Defined ");
        window.FlutterChannel.postMessage(JSON.stringify(data));
    } else {
        console.error("FlutterChannel is not defined or postMessage is not a function.");
    }
}

session.onActivityData((ev) => {
    try {
        if (ev.data && Array.isArray(ev.data.trials) && number_of_trials_count == ev.data.trials.length) {
            const sessionData = calculateTrialStatistics(JSON.parse(JSON.stringify(ev.data)));
            console.log("원본데이터= " + JSON.stringify(ev.data));

            // Flutter로 데이터 전달
            sendDataToFlutter({
                type: "complete",
                sessionData: sessionData
            });

        }
    } catch (error) {
        console.error("Error processing activity data:", error);
    }
});

function calculateTrialStatistics(inputData: InputData) {
    // 1. inputData와 trials 배열이 유효한지 확인
    if (!inputData || !Array.isArray(inputData.trials)) {
        throw new Error("유효하지 않은 inputData입니다.");
    }
    const trials = inputData.trials;

    // 2. 응답 시간(response_time_duration_ms)을 추출 (숫자인 값만 필터링)
    const responseTimes = trials
        .map((trial) => trial.response_time_duration_ms)
        .filter((time) => typeof time === "number");

    // 3. 응답 시간 평균 계산 (배열이 비어있으면 0 반환)
    const meanResponseTime =
        responseTimes.length > 0
            ? responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length
            : 0;

    // 5. 응답 시간 표준 편차 계산 (배열이 비어있으면 0 반환)
    let standardDeviationResponseTime = 0;
    if (responseTimes.length > 0) {
        const variance =
            responseTimes.reduce((sum, time) => sum + Math.pow(time - meanResponseTime, 2), 0) /
            responseTimes.length;
        standardDeviationResponseTime = Math.sqrt(variance);
    }

    // 6. 올바른 시도와 잘못된 시도 필터링
    //    올바른 시도: correct_response_index와 user_response_index가 같은 경우
    const correctTrials = trials.filter(
        (trial) => trial.correct_response_index === trial.user_response_index
    );
    const incorrectTrials = trials.filter(
        (trial) => trial.correct_response_index !== trial.user_response_index
    );

    // 7. EAS 기준 중앙값 계산: 정답 시행 중 RT <= 10,000ms인 값만 사용
    //    10,000ms 초과 제외 → 오답 제외 → 중앙값 산출
    const filteredCorrectResponseTimes = correctTrials
        .map((trial) => trial.response_time_duration_ms)
        .filter((time) => typeof time === "number" && time <= 10000);

    let medianResponseTime = 0;
    if (filteredCorrectResponseTimes.length > 0) {
        const sorted = [...filteredCorrectResponseTimes].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        medianResponseTime = sorted.length % 2 === 0
            ? (sorted[mid - 1] + sorted[mid]) / 2
            : sorted[mid];
    }

    // 8. 전체 시도 수
    const totalTrials = trials.length;

    // 9. 올바른 시도 비율 계산 (전체 시도가 0이면 0 처리)
    const proportionCorrectTrials = totalTrials > 0 ? correctTrials.length / totalTrials : 0;

    // 10. 올바른 시도의 총 개수
    const totalCorrectTrials = correctTrials.length;

    // 11. 품질 관리: 정해진 횟수(11회)가 모두 제출되면 1, 누락이 있으면 0
    const qualityControl = trials.length === number_of_trials_count ? 1 : 0;

    // 12. 결과 객체 생성
    const resultData: any = {
        symrtMn: meanResponseTime / 1000,              // 평균 응답 시간 (ms -> sec)
        symrtMed: medianResponseTime / 1000,           // 중앙값 응답 시간 (ms -> sec)
        symrtSd: standardDeviationResponseTime / 1000, // 표준 편차 응답 시간 (ms -> sec)
        symacc: proportionCorrectTrials,        // 올바른 시도 비율
        symtot: totalCorrectTrials,             // 올바른 시도의 총 개수
        symbolQual: qualityControl,             // 품질 관리 결과
        corrCnt: correctTrials.length           // 정답 개수
    };

    return resultData;
}


session.initialize();
